'use client';

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/shadcn/lib/utils';
import { PropertyCategoryIcon } from '../PropertyCategoryIcons';
import { CHIP_BASE, CHIP_ON, CHIP_OFF } from '@/shared/components/ui/chip';
import { ADVERT_PROPERTY_CATEGORIES } from '../../constants/filterConstants';
import type { ListingPropertySubtype } from '../../../listings/definitions/propertyTypes';

interface Props {
    value: ListingPropertySubtype[];
    onChange: (next: ListingPropertySubtype[]) => void;
}

/**
 * Obsah pulldownu "Nehnutelnost" - kategorie ako accordion, podtypy ako chips.
 *
 * Hlavicka kategorie LEN roztvara (nic neoznacuje), "Vsetky" je samostatny
 * prepinac. Vdaka tomu sa da vybrat aj jediny podtyp bez toho, aby sa najprv
 * musela oznacit cela kategoria.
 *
 * Zdielane medzi inline filtrom a rozsirenym dialogom.
 */
export function PropertyTypeAccordion(props: Props) {
    /** roztvorene kategorie */
    const [openCats, setOpenCats] = useState<number[]>([]);

    return (
        <div className="w-full">
            {ADVERT_PROPERTY_CATEGORIES.map((cat, i) => {
                const selectedCount = cat.types.filter((t) =>
                    props.value.includes(t.key as ListingPropertySubtype),
                ).length;
                const selectedAll = selectedCount === cat.types.length;
                const openCat = openCats.includes(i);

                return (
                    <div
                        key={cat.key}
                        className="w-full border-b border-black/[.06] py-[10px] first:pt-[8px] last:border-b-0 last:pb-[8px]"
                    >
                        {/* hlavicka: klik LEN roztvara, nic neoznacuje */}
                        <div
                            className={cn(
                                'group/cat w-full flex justify-between items-center cursor-pointer select-none',
                                'px-[10px] py-[8px] -mx-[10px] rounded-[16px]',
                                'hover:bg-black/[.028]',
                                'transition duration-[240ms] ease-[var(--ease-rx)]',
                            )}
                            onClick={() =>
                                setOpenCats((prev) =>
                                    prev.includes(i)
                                        ? prev.filter((x) => x !== i)
                                        : [...prev, i],
                                )
                            }
                        >
                            <div className="flex items-center gap-[11px] min-w-0">
                                {/* glyf v kruhu: zlaty ked je nieco vybrate, inak tichy; hover ho jemne zvacsi */}
                                <span
                                    className={cn(
                                        'relative shrink-0 size-[34px] rounded-full flex items-center justify-center',
                                        'transition duration-[260ms] ease-[var(--ease-rx)]',
                                        'group-hover/cat:scale-[1.07]',
                                        selectedCount > 0
                                            ? 'bg-[#F3C300] text-black'
                                            : 'bg-[#f4f3ee] text-black/45 group-hover/cat:bg-[#f8ebb4] group-hover/cat:text-black',
                                    )}
                                >
                                    <PropertyCategoryIcon categoryKey={cat.key} className="size-[19px]" />
                                </span>
                                <p
                                    className={cn(
                                        'font-[500] text-[14px] truncate',
                                        'transition duration-[240ms] ease-[var(--ease-rx)]',
                                        'text-black/85 group-hover/cat:text-black',
                                    )}
                                >
                                    {cat.text}
                                </p>
                                {/* pocitadlo sa vysuva animovane (max-width), nie skokom */}
                                <span
                                    className={cn(
                                        'shrink-0 text-[11px] font-[600] leading-[16px] rounded-full',
                                        'bg-[#f8ebb4] text-black overflow-hidden',
                                        'transition-[max-width,opacity,padding] duration-[280ms] ease-[var(--ease-rx)]',
                                        selectedCount > 0
                                            ? 'max-w-[70px] opacity-100 px-[8px] py-[2px]'
                                            : 'max-w-0 opacity-0 px-0 py-[2px]',
                                    )}
                                >
                                    {selectedAll ? 'všetky' : selectedCount}
                                </span>
                            </div>
                            <div className="flex items-center gap-[12px]">
                                {/* Vsetky - oznacuje az explicitny klik */}
                                <button
                                    type="button"
                                    className={cn(
                                        'flex items-center gap-[7px] cursor-pointer',
                                        'transition duration-200 ease-[var(--ease-rx)]',
                                        selectedAll ? 'text-black' : 'text-black/55 hover:text-black',
                                    )}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (selectedAll) {
                                            props.onChange(
                                                props.value.filter(
                                                    (sKey) =>
                                                        !cat.types.some((t) => t.key === sKey),
                                                ),
                                            );
                                        } else {
                                            props.onChange([
                                                ...new Set([
                                                    ...props.value,
                                                    ...cat.types.map(
                                                        (t) => t.key as ListingPropertySubtype,
                                                    ),
                                                ]),
                                            ]);
                                            setOpenCats((prev) =>
                                                prev.includes(i) ? prev : [...prev, i],
                                            );
                                        }
                                    }}
                                >
                                    <small className="font-[500] text-[12px]">Všetky</small>
                                    <span
                                        className={cn(
                                            'size-[15px] flex justify-center items-center border p-[2px] rounded-full',
                                            'transition duration-200 ease-[var(--ease-rx)]',
                                            selectedAll
                                                ? 'border-cust-primary'
                                                : 'border-black/25 group-hover/cat:border-black/45',
                                        )}
                                    >
                                        <span
                                            className={cn(
                                                'w-full h-full rounded-full bg-cust-primary',
                                                'transition duration-200 ease-[var(--ease-rx)]',
                                                selectedAll
                                                    ? 'opacity-100 scale-100'
                                                    : 'opacity-0 scale-50',
                                            )}
                                        />
                                    </span>
                                </button>
                                <ChevronDown
                                    className={cn(
                                        'size-[15px] text-black/40 group-hover/cat:text-black/75',
                                        'transition duration-[260ms] ease-[var(--ease-rx)]',
                                        openCat && 'rotate-180',
                                    )}
                                />
                            </div>
                        </div>

                        {/* typy ako chips - animovane roztvaranie */}
                        <div
                            className={cn(
                                'grid transition-[grid-template-rows] duration-[300ms] ease-[var(--ease-rx)]',
                                openCat ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                            )}
                        >
                            <div className="overflow-hidden min-h-0">
                                <div className="flex flex-wrap gap-[6px] pt-[12px] pl-[45px]">
                                    {cat.types.map((type) => {
                                        const sel = props.value.includes(
                                            type.key as ListingPropertySubtype,
                                        );
                                        return (
                                            <button
                                                key={type.key}
                                                type="button"
                                                className={cn(CHIP_BASE, sel ? CHIP_ON : CHIP_OFF)}
                                                onClick={() =>
                                                    props.onChange(
                                                        sel
                                                            ? props.value.filter(
                                                                  (t) => t !== type.key,
                                                              )
                                                            : [
                                                                  ...new Set([
                                                                      ...props.value,
                                                                      type.key as ListingPropertySubtype,
                                                                  ]),
                                                              ],
                                                    )
                                                }
                                            >
                                                {type.text}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
